import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { REGIONS } from '@leanin/shared/region';
import type { Region } from '@leanin/shared/region';
import { useMember } from '@/lib/use-session';
import { NetworksChrome } from '@/components/networks/NetworksChrome';
import { GroupsListBody } from '@/components/group/index';
import { JourneyDirectoryHint } from '@/components/entry-journeys';
import { groupSummaryToListItem } from '@/components/networks/adapters';
import type { NetworkListItem } from '@/components/networks/adapters';
import {
  groupKeys,
  type ListNetworksArgs,
  type NetworkFilterArgs,
} from '@/lib/queries/groups';

type NetworksSearch = {
  q?: string;
  region?: ReadonlyArray<Region>;
};

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function parseRegions(v: unknown): ReadonlyArray<Region> | undefined {
  const raw =
    typeof v === 'string'
      ? v.split(',')
      : Array.isArray(v)
        ? v.flatMap((x) => (typeof x === 'string' ? x.split(',') : []))
        : null;
  if (raw === null) return undefined;
  const valid = (REGIONS as readonly string[])
    .filter((r) => raw.includes(r))
    .map((r) => r as Region);
  return valid.length > 0 ? valid : undefined;
}

const PAGE_SIZE = 24;

export const Route = createFileRoute('/_public/networks/')({
  validateSearch: (raw: Record<string, unknown>): NetworksSearch => {
    const q = pickStr(raw.q);
    const region = parseRegions(raw.region);
    return {
      ...(q !== undefined ? { q } : {}),
      ...(region !== undefined ? { region } : {}),
    };
  },
  component: NetworksListPage,
});

function NetworksListPage() {
  const search = Route.useSearch();
  const member = useMember();
  const signedIn = member !== null;

  // The unified directory always queries the full ('all') scope — the My
  // Networks section is driven by the mine roll below, not a list scope.
  const q = search.q?.trim() ?? '';
  const region = search.region ?? [];

  const filterArgs: NetworkFilterArgs = {
    ...(q.length > 0 ? { q } : {}),
    ...(region.length > 0 ? { region: region.join(',') } : {}),
  };
  const listArgs: ListNetworksArgs = { pageSize: PAGE_SIZE, ...filterArgs };

  // Mine keeps memberships out of the All grid. The matching query lists what
  // the "My Networks" section shows: the same request narrowed by the
  // directory's search and region on the server. With neither set the two
  // share one cache entry, so a first filter keeps the list on screen while
  // it loads. Disabled when no session: the endpoint requires auth and the
  // data is irrelevant to the anon directory view.
  const myNetworksQuery = useQuery({
    ...groupKeys.network.mine({ includeArchived: true }),
    enabled: signedIn,
  });
  const myMatchingQuery = useQuery({
    ...groupKeys.network.mine({
      includeArchived: true,
      filters: filterArgs,
    }),
    enabled: signedIn,
  });
  const toListItems = (data: typeof myNetworksQuery.data): readonly NetworkListItem[] | null =>
    data ? data.items.map(groupSummaryToListItem) : null;

  return (
    <NetworksChrome signedIn={signedIn}>
      <JourneyDirectoryHint journey="join-network" />
      <GroupsListBody
        kind="network"
        listArgs={listArgs}
        mine={toListItems(myNetworksQuery.data)}
        mineMatching={toListItems(myMatchingQuery.data)}
        isError={
          (myNetworksQuery.isError && myNetworksQuery.data === undefined) ||
          (myMatchingQuery.isError && myMatchingQuery.data === undefined)
        }
        signedIn={signedIn}
      />
    </NetworksChrome>
  );
}
